"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  BookOpen,
  Sparkles,
  ArrowRight,
  Mic,
  Crown,
  Brain,
  Stethoscope,
  Heart,
  Activity,
  Eye,
  ShieldCheck,
  Compass,
  Network,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { StepperBlock } from "@/lib/blocks/stepper-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

export default function LeadershipAsDiagnosisPage() {
  return (
    <>
      <PageHero
        icon={BookOpen}
        overline="Glossary · Leadership as Diagnosis"
        overlineClassName="text-denim"
        title={
          <>
            The Clinical Operating Framework for <span className="text-gradient-premium">Organizational Leadership</span>
          </>
        }
        subtitle="A clinical operating framework that applies the medical method — the centuries-old discipline of diagnosis, triage, protocol, judgment, and outcome accountability — to organizational leadership. Not a metaphor. The actual operating system for sovereign-scale operations."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Glossary", href: "/glossary/" }, { label: "Leadership as Diagnosis" }]}
        badge="Six principles · Clinical origins"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-5`}>
            <Sparkles className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Definition</span>
          </div>
          <h2 className="text-display-md mb-5">What leadership as diagnosis is.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            <span className="font-semibold text-foreground">Leadership as diagnosis</span> is a clinical operating framework that applies the medical method — the centuries-old discipline of diagnosis, triage, protocol, judgment, and outcome accountability — to organizational leadership. It is not a metaphor. It is the actual operating system that Dr. Jyoti Kush uses to run sovereign-scale operations across eighteen countries.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The framework holds that the six clinical skills that keep patients alive are the same six skills that keep organizations functioning under pressure. Leadership as diagnosis replaces charisma-based leadership with evidence-based leadership. It replaces vision with evidence, rhetoric with protocol, and inspiration with outcome accountability. The physician who treats before diagnosing kills patients. The leader who acts before understanding destroys value.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Stethoscope className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Six Clinical Principles</span>
          </div>
          <h2 className="text-display-md mb-4">Transferable disciplines from clinical practice.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The framework is built on six principles drawn directly from clinical practice. Each is a direct translation of a medical discipline into an organizational operating requirement.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { num: "01", title: "Diagnose Before Treating", body: "The first rule of medicine is the most violated rule of leadership. Gather evidence, assess the situation, and understand the condition before prescribing treatment. Diagnosis is not delay — it is the discipline that makes action effective.", accent: "red" },
            { num: "02", title: "Triage Under Fear", body: "The skill of ranking priorities when the stakes are high, the information is incomplete, and the pressure is immediate. Allocate resources to the most critical need, not the loudest voice. The ability to rank under fear separates leaders who survive from leaders who break.", accent: "purple" },
            { num: "03", title: "Run the Protocol; Keep the Judgment", body: "Protocols exist because individual brilliance is unreliable. The system produces reliable outcomes and individual expertise elevates them, not replaces them. Leadership demands that the protocol be run first and judgment applied second.", accent: "cyan" },
            { num: "04", title: "Own the Outcome, Not the Effort", body: "Medicine does not credit trying. The patient does not care how hard the doctor tried. The patient cares whether they survived. Organizations routinely credit effort over outcome. Leadership as diagnosis demands outcome accountability.", accent: "emerald" },
            { num: "05", title: "First, Do No Harm", body: "The discipline of not making things worse under pressure. Leaders who act under pressure without understanding the consequences produce interventions that create new problems while failing to solve the original one.", accent: "amber-gold" },
            { num: "06", title: "Regulate the Room", body: "The ability to maintain composure so that the team can function. A leader who breaks under pressure breaks the team. A leader who regulates under pressure gives the team permission to perform. Room regulation is a clinical discipline.", accent: "denim" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <span className="text-caption font-bold text-white">{p.num}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Activity className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Delivery Room Parallel</span>
          </div>
          <h2 className="text-display-md mb-4">Why the parallel is structural, not rhetorical.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            In the delivery room, the physician faces a set of conditions remarkably similar to those faced by organizational leaders: incomplete information, irreversible consequences, competing priorities, a team that must function under pressure.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { dim: "Charisma vs. Competence", body: "The delivery room does not reward charisma. It rewards competence. The same applies to leadership as diagnosis.", accent: "red" },
            { dim: "Vision vs. Execution", body: "The delivery room does not reward vision. It rewards execution. The same applies to leadership as diagnosis.", accent: "purple" },
            { dim: "Effort vs. Outcomes", body: "The delivery room does not reward effort. It rewards outcomes. The same applies to leadership as diagnosis.", accent: "cyan" },
            { dim: "Protocol as System", body: "The hospital operates on protocols, checklists, sign-outs, and failure reviews. The same four components are the operating system for excellence.", accent: "emerald" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.dim}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-sm mb-2 ${a.text}`}>{p.dim}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Network className="size-3.5 text-lime" />
            <span className="text-overline text-lime">Related Terms</span>
          </div>
          <h2 className="text-display-md mb-4">The conceptual map.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { term: "Operational Excellence", href: "/glossary/operational-excellence/", desc: "The engineered system the clinical method produces.", accent: "amber-gold" },
            { term: "Strategic Sovereignty", href: "/glossary/strategic-sovereignty/", desc: "The integrated discipline the clinical method enables.", accent: "red" },
            { term: "Sovereign Operations Architecture", href: "/glossary/sovereign-operations-architecture/", desc: "The methodology the clinical method operationalizes.", accent: "emerald" },
            { term: "Crisis Transformation", href: "/glossary/crisis-transformation/", desc: "The perception-pillar discipline under pressure.", accent: "coral" },
            { term: "70% Operating Model", href: "/glossary/70-percent-operating-model/", desc: "The team architecture the clinical method shapes.", accent: "emerald" },
            { term: "Penta P's Framework", href: "/glossary/penta-ps-framework/", desc: "The five-domain model the clinical method operates within.", accent: "cyan" },
          ].map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link href={t.href} className="block h-full group">
                  <div className={`relative glass-card rounded-2xl p-5 h-full bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                    <h3 className={`text-heading-sm mb-2 ${a.text}`}>{t.term}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed mb-3">{t.desc}</p>
                    <span className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                      Read <ArrowRight className="size-3" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Stethoscope className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Diagnostic Process</span>
          </div>
          <h2 className="text-display-md mb-4">The five stages of the diagnostic process.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Leadership as diagnosis operates through a five-stage process — from initial symptom assessment through outcome verification. Each stage mirrors a clinical discipline.
          </p>
        </div>
        <StepperBlock
          steps={[
            { num: "01", title: "Symptom Assessment", body: "The first stage mirrors the clinical intake. Every observable indicator is documented — performance deviations, organizational dysfunction, stakeholder signals, operational metrics. The symptoms are mapped without premature interpretation.", accent: "cyan" },
            { num: "02", title: "Differential Diagnosis", body: "The symptoms are evaluated against multiple possible conditions. The leader who jumps to a single cause produces a fragile diagnosis. Differential diagnosis considers competing explanations and tests each against the evidence.", accent: "purple" },
            { num: "03", title: "Root Cause Identification", body: "The underlying condition is identified through systematic investigation. The root cause is rarely the most visible symptom. The leader who treats the symptom without identifying the root cause produces a temporary fix that recurs.", accent: "emerald" },
            { num: "04", title: "Intervention Protocol", body: "A calibrated intervention is prescribed based on the diagnosis. The protocol specifies the sequence, the inputs, the validation steps, and the escalation paths. Protocol with judgment: follow the procedure while retaining the capacity to deviate when conditions demand.", accent: "amber-gold" },
            { num: "05", title: "Outcome Verification", body: "The intervention is monitored and the outcome is verified against the diagnosis. The leader who commands the outcome owns it completely. Outcome accountability is the discipline that closes the diagnostic loop.", accent: "red" },
            { num: "06", title: "Institutional Learning", body: "The diagnostic case is documented for institutional knowledge. Future diagnoses benefit from the accumulated evidence. The compounding effect is what makes the clinical method an operating system rather than a project.", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Compass className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Usage Examples</span>
          </div>
          <h2 className="text-display-md mb-4">The clinical method in practice.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four examples of leadership as diagnosis applied to organizational challenges. Each example shows the clinical method producing verified outcomes.
          </p>
        </div>
        <FeatureGridBlock
          features={[
            { title: "Organizational Underperformance", body: "A division underperforming against targets. Symptom assessment: declining metrics, stakeholder complaints, talent attrition. Differential diagnosis: leadership vacuum, structural misalignment, or market shift. Root cause: structural misalignment. Intervention: redesign of the operating architecture. Outcome: metrics restored within two quarters.", accent: "red" },
            { title: "Crisis Recovery", body: "A Fortune 500 corporation under reputational attack. Symptom assessment: media coverage, public sentiment, regulatory scrutiny. Differential diagnosis: organic criticism versus coordinated attack. Root cause: coordinated attack. Intervention: crisis response engine activation. Outcome: reputation exceeds the pre-crisis baseline within 90 days.", accent: "purple" },
            { title: "Governance Transformation", body: "A national government transforming citizen service delivery. Symptom assessment: service delays, citizen complaints, operational inefficiency. Root cause: legacy systems and fragmented processes. Intervention: eGovernance deployment with six-layer architecture. Outcome: up to 120M citizens served in a single deployment.", accent: "cyan" },
            { title: "Political Campaign Recovery", body: "A campaign trailing in the polls with limited time remaining. Symptom assessment: unfavorable sentiment, weak ground game, message fatigue. Differential diagnosis: tactical versus strategic issues. Root cause: tactical. Intervention: ground truth deployment and message recalibration. Outcome: campaign victory.", accent: "emerald" },
          ]}
          columns={2}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <BookOpen className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Common Misconceptions</span>
          </div>
          <h2 className="text-display-md mb-4">What leadership as diagnosis is not.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six persistent misconceptions about the clinical method applied to leadership. The misconceptions are common because the metaphor of leadership-as-medicine is often used loosely.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "Is leadership as diagnosis just a metaphor?", a: "No. The framework is not a metaphor — it is the actual operating system used in sovereign-scale operations across eighteen countries. The medical training is not displayed as a credential; it is the foundation of the operating methodology." },
            { q: "Does leadership as diagnosis require medical training?", a: "Not necessarily. The framework can be applied by leaders from any background who master the diagnostic discipline. The medical training of the framework's originator provides the foundation, but the discipline itself is teachable." },
            { q: "Is the clinical method slow?", a: "No. Diagnosis is not delay — it is the discipline that makes action effective. Acting without diagnosis is improvisation, not leadership. The clinical method produces faster, more accurate outcomes than intuition-based approaches." },
            { q: "Does the framework eliminate the role of judgment?", a: "No. The clinical method requires protocol with judgment. Protocols exist because individual brilliance is unreliable. The system produces reliable outcomes, and individual expertise elevates them. Judgment operates within the protocol, not in place of it." },
            { q: "Is the framework only for crisis situations?", a: "No. The clinical method applies to all leadership decisions — strategic, operational, organizational, and interpersonal. The discipline of diagnose-before-treating produces superior outcomes in every domain, not just under pressure." },
            { q: "Can the framework be combined with other leadership approaches?", a: "Yes. The clinical method integrates with strategic leadership, transformational leadership, and other frameworks. The discipline adds the diagnostic rigor and outcome accountability that other approaches often lack. The integration produces a more complete operating system." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-denim/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Brain className="size-10 text-denim mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage the clinical method.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Leadership as diagnosis is the operating system that produces sovereign-scale outcomes. Engagements — executive advisory, board service, consulting, mentorship — apply the clinical method to organizational challenges.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/glossary/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <BookOpen className="size-4" />
                Glossary Hub
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Glossary Hub", description: "Ten terms defining sovereign-scale operations.", href: "/glossary/", icon: BookOpen, accent: "cyan" },
          { title: "FAQ: Leadership", description: "Clinical leadership at sovereign scale.", href: "/faq/leadership/", icon: Crown, accent: "emerald" },
          { title: "About Dr. Jyoti Kush", description: "The full story of the operational architect.", href: "/about/", icon: Crown, accent: "red" },
          { title: "The 70% Project", description: "The flagship initiative on women in leadership.", href: "/the-70-percent-project/", icon: Heart, accent: "coral" },
        ]}
      />
    </>
  );
}
